<template>
    <div class="tabview">
        <!-- Tab Header for 'tab' type -->
        <div v-if="props.type === 'tab'" class="tab-header">
            <div v-for="(tab, index) in tabs" :class="{
                'tab-header-item': true,
                'selected': selectedTab === index,
                'hidden': hiddenTabs.includes(getIdentifier(tab.component)),
                'disabled': disabledTabs.includes(getIdentifier(tab.component))
            }" :key="index">
                <button :disabled="disabledTabs.includes(getIdentifier(tab.component))" @click="selectTab(index)">{{ tab.title }}</button>
            </div>
        </div>
        
        <!-- Selection List for 'list' type -->
        <div v-else-if="props.type === 'list'" class="tab-list-container">
            <select v-model="selectedTab" @change="selectTab(selectedTab)" class="tab-select">
                <option 
                    v-for="(tab, index) in tabs" 
                    :value="index" 
                    :key="index"
                    v-show="!hiddenTabs.includes(getIdentifier(tab.component))"
                    :disabled="disabledTabs.includes(getIdentifier(tab.component))"
                >
                    {{ tab.title }}
                </option>
            </select>
        </div>
        
        <slot></slot>
    </div>
</template>

<script setup>
import { ref, provide, onMounted, useSlots, getCurrentInstance, watch } from 'vue'
import ZSTab from './ZSTab.vue'

const props = defineProps({
    identifier: String,
    selectedIndex: {
        type: Number,
        default: -1
    },
    type: {
        type: String,
        default: 'tab',
        validator: (value) => ['tab', 'list'].includes(value)
    }
})

const emit = defineEmits(['tabChanged'])
const slots = useSlots()

const tabs = ref([])
const selectedTab = ref(0)
const hiddenTabs = ref([])
const disabledTabs = ref([])

const getIdentifier = (component) => component?.identifier?.value ?? component?.identifier

const getFirstSelectableTabIndex = () => {
    return tabs.value.findIndex((tab) => {
        const identifier = getIdentifier(tab.component)
        return !hiddenTabs.value.includes(identifier) && !disabledTabs.value.includes(identifier)
    })
}

const selectTab = (index) => {
    if (!tabs.value[index]) return

    const child = tabs.value[index].component
    if (!child) return

    const identifier = getIdentifier(child)
    if (disabledTabs.value.includes(identifier)) return

    tabs.value.forEach(t => {
        if (t?.component?.selectTab && typeof t.component.selectTab === 'function') {
            t.component.selectTab(identifier)
        }
    })
    selectedTab.value = index
    emit('tabChanged', index)
}

const setTabVisibility = (identifier, visible) => {
    const tab = tabs.value.find(tab => getIdentifier(tab.component) === identifier)
    if (tab) {
        if (!visible) {
            if (!hiddenTabs.value.includes(identifier)) {
                hiddenTabs.value.push(identifier)
            }
        } else {
            hiddenTabs.value = hiddenTabs.value.filter(id => id !== identifier)
        }

        const currentIdentifier = getIdentifier(tabs.value[selectedTab.value]?.component)
        if (currentIdentifier && hiddenTabs.value.includes(currentIdentifier)) {
            const nextIndex = getFirstSelectableTabIndex()
            if (nextIndex > -1) {
                selectTab(nextIndex)
            }
        }
    }
}

provide('setTabVisibility', setTabVisibility)

const setTabDisabled = (identifier, disabled) => {
    const tab = tabs.value.find(tab => getIdentifier(tab.component) === identifier)
    if (!tab) return

    if (disabled) {
        if (!disabledTabs.value.includes(identifier)) {
            disabledTabs.value.push(identifier)
        }
    } else {
        disabledTabs.value = disabledTabs.value.filter(id => id !== identifier)
    }

    const currentIdentifier = getIdentifier(tabs.value[selectedTab.value]?.component)
    if (currentIdentifier && disabledTabs.value.includes(currentIdentifier)) {
        const nextIndex = getFirstSelectableTabIndex()
        if (nextIndex > -1) {
            selectTab(nextIndex)
        }
    }
}

provide('setTabDisabled', setTabDisabled)

// Add a method to update tab titles
const updateTabTitle = (id, newTitle) => {
    const tabIndex = tabs.value.findIndex(tab => getIdentifier(tab.component) === id)
    if (tabIndex !== -1 && tabs.value[tabIndex].title !== newTitle) {
        tabs.value[tabIndex].title = newTitle
    }
}

// Provide the method to child components
provide('updateTabTitle', updateTabTitle)

onMounted(() => {
    const instance = getCurrentInstance();
    const childComponent = instance.subTree.children.find(child => child.key === "_default")

    slotContent.value = childComponent?.children ?? []
    refresh()
})

const slotContent = ref([]);

const refresh = () => {
    // const slotContent = slots.default?.()


    if (slotContent.value && Array.isArray(slotContent.value)) {
        let counter = 0
        Array.from(slotContent.value).forEach((vnode) => {
            // Skip null or undefined nodes
            if (!vnode || !vnode.type) return

            // Check if it's a ZSTab component
            // console.log(vnode);

            if (vnode.type.__name === 'ZSTab') {
                // In Vue 3, use vnode.component to access the component instance
                if (vnode.component && vnode.component.exposed) {
                    const exposed = vnode.component.exposed

                    // Create tab component with exposed properties
                    const tabComponent = {
                        setId: exposed.setId,
                        identifier: exposed.identifier,
                        selectTab: exposed.selectTab
                    }

                    if (typeof tabComponent.setId === 'function') {
                        tabComponent.setId(counter)
                    }

                    tabs.value.push({
                        title: vnode.props?.title || `Tab ${counter + 1}`,
                        component: tabComponent
                    })

                    const tabIdentifier = getIdentifier(tabComponent)

                    if (vnode.props && vnode.props.visible === false && !hiddenTabs.value.includes(tabIdentifier)) {
                        hiddenTabs.value.push(tabIdentifier)
                    }

                    if (vnode.props && vnode.props.disabled === true && !disabledTabs.value.includes(tabIdentifier)) {
                        disabledTabs.value.push(tabIdentifier)
                    }

                    counter++
                }
            }
        })

        if (props.selectedIndex > -1 && props.selectedIndex < tabs.value.length) {
            selectedTab.value = props.selectedIndex
        }

        if (tabs.value.length > 0) {
            const nextIndex = getFirstSelectableTabIndex()
            if (nextIndex > -1) {
                selectedTab.value = nextIndex
                selectTab(selectedTab.value)
            }
        }
    }
}

watch(() => props.selectedIndex, (newIndex) => {
    if (newIndex > -1 && newIndex < tabs.value.length) {
        selectedTab.value = props.selectedIndex
        selectTab(selectedTab.value)
    }
})

defineExpose({
    refresh
})
</script>

<style>
:root {
    --zs-tabview-header-display: flex;
    --zs-tabview-header-align-items: center;
    --zs-tabview-header-bg-color: var(--color-tabview-header-bg);
    --zs-tabview-header-min-height: 40px;
    --zs-tabview-header-margin-bottom: 5px;
    --zs-tabview-header-width: 100%;
    --zs-tabview-button-max-width: 300px;
    --zs-tabview-button-bg-color: transparent;
    --zs-tabview-button-border: none;
    --zs-tabview-button-padding: 10px 0px;
    --zs-tabview-button-margin-right: 20px;
    --zs-tabview-button-border-radius: 0px;
    --zs-tabview-button-color: var(--color-tabview-button);
    --zs-tabview-button-font-family: "Poppins";
    --zs-tabview-button-font-size: 14px;
    --zs-tabview-button-cursor: pointer;
    --zs-tabview-button-border-bottom: 2px solid transparent;
    --zs-tabview-button-white-space: nowrap;
    --zs-tabview-button-overflow: hidden;
    --zs-tabview-button-text-overflow: ellipsis;
    --zs-tabview-button-hover-color: var(--color-black);
    --zs-tabview-button-hover-border-bottom: 2px solid var(--color-blue);
    --zs-tabview-selected-button-color: var(--color-black);
    --zs-tabview-selected-button-font-family: "Poppins";
    --zs-tabview-selected-button-border-bottom: 2px solid var(--color-blue);
}

/* .tabview {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 
    background-color: green;
} */

.tab-header {
    /* display: var(--zs-tabview-header-display);
    align-items: var(--zs-tabview-header-align-items);
    background-color: var(--zs-tabview-header-bg-color);
    min-height: var(--zs-tabview-header-min-height);
    margin-bottom: var(--zs-tabview-header-margin-bottom);
    width: var(--zs-tabview-header-width); */

    display: flex;
    align-items:  center;
    background-color: var(--zs-tabview-header-bg-color);
    min-height: var(--zs-tabview-header-min-height);
    margin-bottom: var(--zs-tabview-header-margin-bottom);
    width: var(--zs-tabview-header-width);
    border-bottom:1px solid lightgray;

    .tab-header-item {
        button {
            position: relative;
            top:1px;
            max-width: var(--zs-tabview-button-max-width);
            background-color: var(--zs-tabview-button-bg-color);
            border: 2px solid transparent;
            padding: 0.5em 1em;
            margin-right: 0.2em;
            border-radius: 8px 8px 0 0;
            color: var(--zs-tabview-button-color);
            font-family: var(--zs-tabview-button-font-family);
            font-size: 1.1em;
            font-weight: 600;
            cursor: var(--zs-tabview-button-cursor);
            border-bottom: var(--zs-tabview-button-border-bottom);
            white-space: var(--zs-tabview-button-white-space);
            overflow: var(--zs-tabview-button-overflow);
            text-overflow: var(--zs-tabview-button-text-overflow);

            &:hover {
                color: black;
                /* font-family: "Satoshi-Bold"; */
               
                /* border-bottom: var(--zs-tabview-button-hover-border-bottom); */
            }
        }

        &.selected {
            button {
                color: var(--zs-tabview-selected-button-color);
                font-family: var(--zs-tabview-selected-button-font-family);
               border-color:lightgray;
               border-bottom: 2px solid white;
             }
        }

        &.disabled {
            button {
                opacity: 0.45;
                cursor: not-allowed;
            }
        }
    }
}

.hidden {
    display: none;
}

/* Styles for list type */
.tab-list-container {
    margin-bottom: var(--zs-tabview-header-margin-bottom);
    width: 100%;
}

.tab-select {
    width: 100%;
    padding: 0.75em 1em;
    border: 1px solid lightgray;
    border-radius: 8px;
    background-color: white;
    color: var(--zs-tabview-button-color);
    font-family: var(--zs-tabview-button-font-family);
    font-size: var(--zs-tabview-button-font-size);
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s ease;
}

.tab-select:hover,
.tab-select:focus {
    border-color: var(--color-blue);
}

.tab-select option {
    padding: 0.5em;
}
</style>
